<template>
  <canvas ref="canvasRef" class="pointer-events-none fixed inset-0 z-50" aria-hidden="true"></canvas>
</template>

<script setup>
// Single global confetti overlay — mounted once by TasksChecklist.vue, not
// per-row. It MUST live outside any individual TaskRow: the row that
// completes is removed from openItems in the very same reactive tick its
// status flips (see openItems' filter in TasksChecklist.vue), so a
// per-row canvas would be unmounted before or during its own burst — that
// was tried first and never visibly played anything. Bursting from a
// persistent, page-level canvas at the click's viewport coordinates
// sidesteps the row's lifecycle entirely.
import { ref, onMounted, onBeforeUnmount } from 'vue'

const canvasRef = ref(null)
let rafId = null
let ctx = null
let dpr = window.devicePixelRatio || 1

function resize() {
  const canvas = canvasRef.value
  if (!canvas) return
  dpr = window.devicePixelRatio || 1
  canvas.width = window.innerWidth * dpr
  canvas.height = window.innerHeight * dpr
  ctx = canvas.getContext('2d')
}

onMounted(() => {
  resize()
  window.addEventListener('resize', resize)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
  cancelAnimationFrame(rafId)
})

const BASE_COLORS = ['#f59e0b', '#10b981', '#ec4899', '#3b82f6']

// clientX/clientY — viewport coordinates, straight from the triggering
// MouseEvent (TasksChecklist.vue passes `event.clientX/clientY`). accentColor
// (the task's own color) is folded into the palette so the burst reads as
// belonging to that specific task, not a generic effect.
function burst(clientX, clientY, accentColor) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  if (!ctx) resize()
  if (!ctx) return

  const cx = clientX * dpr
  const cy = clientY * dpr
  const palette = accentColor ? [accentColor, ...BASE_COLORS] : BASE_COLORS
  const particles = Array.from({ length: 20 }, () => {
    const angle = Math.random() * Math.PI * 2
    const speed = (1.6 + Math.random() * 2.4) * dpr
    return {
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 1.6 * dpr, // slight upward bias so it reads as a "pop"
      size: (2 + Math.random() * 2.5) * dpr,
      color: palette[Math.floor(Math.random() * palette.length)],
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.4,
    }
  })

  const gravity = 0.14 * dpr
  const start = performance.now()
  const duration = 750
  const canvas = canvasRef.value

  cancelAnimationFrame(rafId)
  function tick(now) {
    const t = Math.min((now - start) / duration, 1)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    particles.forEach((p) => {
      p.vy += gravity
      p.x += p.vx
      p.y += p.vy
      p.rotation += p.spin

      ctx.save()
      ctx.globalAlpha = Math.max(1 - t, 0)
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rotation)
      ctx.fillStyle = p.color
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size)
      ctx.restore()
    })

    if (t < 1) {
      rafId = requestAnimationFrame(tick)
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }
  rafId = requestAnimationFrame(tick)
}

defineExpose({ burst })
</script>
